import { useEffect, useState } from 'react';
import { sms } from '@/api/sms';
import type { SmsConfigView } from '@/api/sms';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle, ExternalLink, Copy, Check } from 'lucide-react';

/**
 * Super-admin page for configuring outbound/inbound SMS via Telnyx.
 *
 * Two secrets are stored encrypted at rest in platform_settings and are
 * never returned to the portal: the Messaging API key (outbound) and the
 * webhook public key (inbound signature verification). Both inputs are
 * write-only — when the backend reports `has_creds` / `has_public_key`,
 * we show a "configured" hint and only send a new value when the operator
 * types one. The Messaging Profile ID and default from-number are not
 * secret, so the GET view returns them and we pre-fill them.
 *
 * Mirrors the CNAM settings page (CnamSettings.tsx) in structure.
 */
export function SmsSettings() {
  const [current, setCurrent] = useState<SmsConfigView | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [profileId, setProfileId] = useState('');
  const [defaultFrom, setDefaultFrom] = useState('');
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  function hydrate(v: SmsConfigView) {
    setCurrent(v);
    setProfileId(v.messaging_profile_id ?? '');
    setDefaultFrom(v.default_from ?? '');
  }

  useEffect(() => {
    let ignore = false;
    sms.getConfig().then((v) => {
      if (ignore) return;
      hydrate(v);
    }).catch(() => { /* leave defaults — likely not yet configured */ });
    return () => { ignore = true; };
  }, []);

  async function save() {
    setStatus(null);
    setSaving(true);
    try {
      await sms.saveConfig({
        provider: 'telnyx',
        messaging_profile_id: profileId.trim(),
        default_from: defaultFrom.trim() || null,
        // Write-only secrets: only include when the operator typed a new value.
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
        ...(publicKey.trim() ? { public_key: publicKey.trim() } : {}),
      });
      setStatus({ kind: 'ok', text: 'Saved. Outbound texts now send through Telnyx and inbound replies are verified.' });
      setApiKey('');
      setPublicKey('');
      const next = await sms.getConfig();
      hydrate(next);
    } catch (err) {
      setStatus({ kind: 'error', text: err instanceof Error ? err.message : 'Save failed.' });
    } finally {
      setSaving(false);
    }
  }

  // Profile ID is always required. The API key is required only on first
  // setup — once stored, the operator can edit the profile / from-number
  // without re-entering it.
  const canSave = profileId.trim().length > 0 && (current?.has_creds || apiKey.trim().length > 0);

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="SMS (Telnyx)"
        description="Send and receive text messages through Telnyx. Used for the SMS conversations view, STOP-reply handling, and any IVR node that texts a caller."
      />
      <div className="max-w-2xl space-y-6">
        <Card className={current?.configured ? 'border-emerald-200/60' : 'border-amber-200/60'}>
          <CardContent className="py-4">
            {current?.configured ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-foreground">SMS is active (Telnyx)</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Profile <span className="font-mono">{current.messaging_profile_id ?? '—'}</span>
                    {current.default_from && (
                      <> &bull; default from <span className="font-mono">{current.default_from}</span></>
                    )}
                    {' '}&bull; API key {current.has_creds ? 'set' : 'missing'}
                    {' '}&bull; webhook key {current.has_public_key ? 'set' : 'missing'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-foreground">SMS is not configured</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Outbound sends will fail and inbound webhooks are rejected until you add a Telnyx
                    API key and Messaging Profile below.
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Telnyx credentials</CardTitle>
            <CardDescription>
              The API key and webhook public key are stored encrypted at rest and never shown back —
              to rotate either one, paste a new value and save. Leave a secret field blank to keep
              the stored value.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="smsApiKey">Messaging API key</Label>
              <Input
                id="smsApiKey"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={current?.has_creds ? '•••••••••••••••• (configured)' : 'KEYxxxxxxxxxxxxxxxx'}
                autoComplete="off"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                Telnyx Mission Control &rarr; <strong>API Keys</strong>. Used to send outbound texts.
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="smsProfile">Messaging Profile ID</Label>
              <Input
                id="smsProfile"
                value={profileId}
                onChange={(e) => setProfileId(e.target.value)}
                placeholder="400xxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="font-mono"
                autoComplete="off"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                Telnyx Mission Control &rarr; <strong>Messaging &rarr; Messaging Profiles</strong>.
                This profile owns your sending numbers and points inbound webhooks at Ivrello.
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="smsFrom">Default from-number <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="smsFrom"
                value={defaultFrom}
                onChange={(e) => setDefaultFrom(e.target.value)}
                placeholder="+15551112222"
                className="font-mono"
                autoComplete="off"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                E.164. Used as the sending number when a message or node doesn&rsquo;t specify one.
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="smsPublicKey">Webhook public key</Label>
              <Input
                id="smsPublicKey"
                type="password"
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                placeholder={current?.has_public_key ? '•••••••••••••••• (configured)' : 'Base64 Ed25519 public key'}
                autoComplete="off"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                Telnyx Mission Control &rarr; <strong>Account &rarr; Public Key</strong>. Used to
                verify the signature on inbound messages and delivery receipts.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button onClick={save} disabled={saving || !canSave}>
                {saving ? 'Saving…' : 'Save settings'}
              </Button>
              {status && (
                <p
                  role="alert"
                  className={status.kind === 'ok' ? 'text-sm text-emerald-600' : 'text-sm text-destructive'}
                >
                  {status.text}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Setup &amp; webhook</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <ol className="list-decimal ps-5 space-y-2">
              <li>
                Sign up at{' '}
                <a
                  href="https://telnyx.com/sign-up"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                >
                  telnyx.com/sign-up <ExternalLink className="h-3 w-3" />
                </a>
                {' '}and buy at least one SMS-capable number.
              </li>
              <li>
                Create an <strong>API key</strong> (Account &rarr; API Keys) and paste it above.
              </li>
              <li>
                Create a <strong>Messaging Profile</strong> (Messaging &rarr; Messaging Profiles),
                assign your numbers to it, and copy its ID into the field above.
              </li>
              <li>
                In that profile&rsquo;s <strong>Inbound settings</strong>, set the webhook URL to the
                address below and the format to <strong>Webhook (Common, v2)</strong>.
              </li>
              <li>
                Copy your account&rsquo;s <strong>Public Key</strong> (Account &rarr; Public Key) into
                the webhook-key field so Ivrello can verify inbound signatures.
              </li>
            </ol>

            <WebhookUrl />

            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5">
              <div><strong>Outbound:</strong> queued through a background worker and retried on transient
                Telnyx errors. STOP/UNSUBSCRIBE replies auto-add the sender to the Do-Not-Contact list.</div>
              <div><strong>Inbound:</strong> the webhook above delivers replies and delivery receipts.
                Signatures are verified with the public key — without it, inbound is rejected.</div>
              <div><strong>Scope:</strong> this is a single platform-wide Telnyx account. Per-reseller carrier
                routing lives under Carriers; this page is the default sender.</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

/**
 * Shows the inbound webhook URL the operator must paste into Telnyx, derived
 * from the live portal origin so it's correct on any deployment / domain.
 */
function WebhookUrl() {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/api/sms/telnyx/webhook`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — operator can still select the text */ }
  }

  return (
    <div className="rounded border border-slate-200 bg-white p-3">
      <div className="text-xs font-medium text-foreground mb-1">Inbound webhook URL</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-slate-100 px-2 py-1.5 text-xs font-mono text-slate-700">
          {url}
        </code>
        <Button type="button" size="sm" variant="outline" onClick={copy} className="shrink-0">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          <span className="ms-1.5">{copied ? 'Copied' : 'Copy'}</span>
        </Button>
      </div>
    </div>
  );
}
